import { defineField, defineType } from "sanity";

export const bagPage = defineType({
  name: "bagPage",
  title: "Bag Page",
  type: "document",
  groups: [
    { name: "content", title: "Content", default: true },
    { name: "modal", title: "Contact Modal" },
    { name: "seo", title: "SEO" }
  ],
  fields: [
    // ── Content ────────────────────────────────────────────────────────
    defineField({
      name: "heroImage",
      title: "Hero image",
      type: "image",
      description: "Hero image at the top of the Bag page.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers."
        })
      ],
      group: "content"
    }),
    defineField({
      name: "heading",
      title: "Page heading",
      type: "string",
      description: "Main heading (e.g. 'YOUR BAG').",
      group: "content"
    }),
    defineField({
      name: "subtitle",
      title: "Subtitle",
      type: "text",
      rows: 2,
      description: "Supporting copy shown when there are items in the bag.",
      group: "content"
    }),
    defineField({
      name: "emptyMessage",
      title: "Empty bag message",
      type: "text",
      rows: 3,
      description: "Text shown when the bag is empty.",
      group: "content"
    }),
    defineField({
      name: "contactButtonText",
      title: "Contact button text",
      type: "string",
      description: "Text on the contact to purchase button (e.g. 'Contact to purchase').",
      group: "content"
    }),

    // ── Contact Modal ──────────────────────────────────────────────────
    defineField({
      name: "modalEyebrow",
      title: "Modal eyebrow",
      type: "string",
      description: "Small label at the top of the contact modal (e.g. 'Contact to purchase').",
      group: "modal"
    }),
    defineField({
      name: "modalHeading",
      title: "Modal heading",
      type: "string",
      description: "Heading inside the contact modal (e.g. 'Send Dana a note').",
      group: "modal"
    }),
    defineField({
      name: "modalBody",
      title: "Modal body",
      type: "text",
      rows: 2,
      description: "Supporting text inside the contact modal.",
      group: "modal"
    }),
    defineField({
      name: "requestedPiecesLabel",
      title: "Requested pieces label",
      type: "string",
      description: "Label above the list of requested pieces in the modal.",
      group: "modal"
    }),
    defineField({
      name: "emailLabel",
      title: "Email label",
      type: "string",
      description: "Label for the email field (e.g. 'Email (required)').",
      group: "modal"
    }),
    defineField({
      name: "emailPlaceholder",
      title: "Email placeholder",
      type: "string",
      description: "Placeholder for the email field (e.g. 'you@example.com').",
      group: "modal"
    }),
    defineField({
      name: "firstNameLabel",
      title: "First name label",
      type: "string",
      description: "Label for the first name field.",
      group: "modal"
    }),
    defineField({
      name: "firstNamePlaceholder",
      title: "First name placeholder",
      type: "string",
      description: "Placeholder for the first name field.",
      group: "modal"
    }),
    defineField({
      name: "lastNameLabel",
      title: "Last name label",
      type: "string",
      description: "Label for the last name field.",
      group: "modal"
    }),
    defineField({
      name: "lastNamePlaceholder",
      title: "Last name placeholder",
      type: "string",
      description: "Placeholder for the last name field.",
      group: "modal"
    }),
    defineField({
      name: "emailBodyLabel",
      title: "Email body label",
      type: "string",
      description: "Label for the email body textarea.",
      group: "modal"
    }),
    defineField({
      name: "defaultEmailBody",
      title: "Default email body",
      type: "text",
      rows: 3,
      description: "Pre-filled text in the email body textarea.",
      group: "modal"
    }),
    defineField({
      name: "sendButtonText",
      title: "Send button text",
      type: "string",
      description: "Text on the send email button (e.g. 'Send email').",
      group: "modal"
    }),
    defineField({
      name: "sendingButtonText",
      title: "Sending button text",
      type: "string",
      description: "Text shown while the email is sending (e.g. 'Sending...').",
      group: "modal"
    }),
    defineField({
      name: "successMessage",
      title: "Success message",
      type: "string",
      description: "Toast message shown after a successful email (e.g. 'Email sent.').",
      group: "modal"
    }),
    defineField({
      name: "emptyBagModalMessage",
      title: "Empty bag modal message",
      type: "string",
      description: "Message shown in modal when the bag is empty.",
      group: "modal"
    }),

    // ── SEO ────────────────────────────────────────────────────────────
    defineField({
      name: "metaTitle",
      title: "Meta title",
      type: "string",
      description: "Page title for search engines.",
      group: "seo"
    }),
    defineField({
      name: "metaDescription",
      title: "Meta description",
      type: "text",
      rows: 3,
      description: "Short description for search engines.",
      group: "seo"
    }),
    defineField({
      name: "ogImage",
      title: "Sharing image",
      type: "image",
      description: "Image shown when this page is shared on social media.",
      options: { hotspot: true },
      fields: [
        defineField({
          name: "alt",
          title: "Alt text",
          type: "string",
          description: "Describe the image for screen readers."
        })
      ],
      group: "seo"
    })
  ],
  preview: {
    prepare: () => ({
      title: "Bag Page"
    })
  }
});
